import { clipAbove, widthAt, type Pt } from "../math/polygon";
import { fmt } from "../format/number";
import { buildSection } from "../geometry/section";
import { jointLevelsOf } from "../geometry/parametric";
import { ledgeLevels } from "../geometry/sketch";
import { assembleCase, type CaseParams } from "../loads/assemble";
import type { WallInput } from "../types/input";
import type { JointCaseResult, JointResult } from "../types/result";
import type { Warn } from "./stability";

const EPS = 1e-6;
/** Derz kotundaki köşenin üst bloğa ait sayılma payı (m) */
const EDGE_TOL = 1e-3;

/** Çokgen modunda derz kotları: gövde tabanı + (verilmişse) elle kotlar; kot listesi boşsa yatay kademe kenarları otomatik. */
export function jointLevelsFor(input: WallInput, baseY: number, yTop: number): number[] {
  const g = input.geometry;
  if (g.mode === "rc-cantilever") return [];
  const raw: number[] = g.mode === "parametric" ? jointLevelsOf(g)
    : [...(g.bodyBaseY !== undefined ? [g.bodyBaseY] : []), ...(g.jointLevels?.length ? g.jointLevels : ledgeLevels(g.points))];
  raw.push(...input.checks.extraJointLevels);
  const inRange = raw.filter((y) => y > baseY + EPS && y < yTop - EPS).map((y) => Math.round(y * 1e6) / 1e6);
  return [...new Set(inRange)].sort((a, b) => a - b);
}

export function analyzeJoint(input: WallInput, polygon: Pt[], level: number, cases: CaseParams[], betaRad: number, warn: Warn): JointResult {
  const w = widthAt(polygon, level + EPS);   // yatay basamak kenarında üst bloğun genişliği (hafif yukarıdan)
  if (!w) throw new Error(`Kesit kotu ${fmt(level)} m için üst blok oluşturulamadı`);
  // Tam kotta kırp; taban çizgisinde kalan ama üst bloğa ait olmayan basamak köşelerini at. Genişlik EPS yukarıda ölçüldüğünden
  // yatık yüzde (eğim dx/dy > 1, ör. geniş tabanlı kademeli gövde) kırpma köşesi xMax'ın eğim·EPS dışında kalır → 1 mm pay
  const upper = clipAbove(polygon, level).filter((p) => Math.abs(p.y - level) > EPS || (p.x >= w.xMin - EDGE_TOL && p.x <= w.xMax + EDGE_TOL));
  if (upper.length < 3) throw new Error(`Kesit kotu ${fmt(level)} m için üst blok oluşturulamadı`);
  const section = buildSection(upper, betaRad);
  const b = section.B;
  const label = `Kesit y = ${fmt(level)} m`;
  const result: JointResult = { level, label, width: b, cases: {}, allOk: true };
  const cs = input.checks.classic;
  const m = input.material;

  for (const cp of cases) {
    const isSeismic = cp.id !== "static";
    const loads = assembleCase({ input, section, origin: section.toe, includeFoundationEffects: false, warn }, cp);
    const N = loads.sums.N;
    const V = loads.sums.Hdrive - loads.sums.Hresist;
    const Mfront = loads.sums.Mres - loads.sums.Movr;         // ön nokta etrafında net moment (direnç yönü +)
    const xbar = N > 1e-9 ? Mfront / N : NaN;
    const e = b / 2 - xbar;                                    // kesit ortasından dış merkezlilik (ön tarafa +)
    const M = N * e;
    const divisor = isSeismic ? cs.jointEccentricity.seismic : cs.jointEccentricity.static;
    const eLimit = b / divisor;
    const ae = Math.abs(e);
    let sigmaMax: number, sigmaMin: number;
    if (!(N > 1e-9) || xbar <= 0 || xbar >= b) { sigmaMax = Infinity; sigmaMin = 0; }
    else if (ae <= b / 6 + 1e-9) { sigmaMax = (N / b) * (1 + (6 * ae) / b); sigmaMin = (N / b) * (1 - (6 * ae) / b); }
    else { sigmaMax = (2 * N) / (3 * (b / 2 - ae)); sigmaMin = 0; }
    const shearTarget = isSeismic ? cs.jointShear.seismic : cs.jointShear.static;
    const shearRes = N * m.jointFriction + m.jointCohesion * b;
    const shearFactor = V > 1e-12 ? shearRes / V : Infinity;
    const eOk = Number.isFinite(sigmaMax) && ae <= eLimit + 1e-9;
    const compressionOk = sigmaMax <= m.allowableCompression + 1e-9;
    const shearOk = shearFactor >= shearTarget;
    const ok = eOk && compressionOk && shearOk;
    const verdict = `${ok ? "SAĞLIYOR" : "SAĞLAMIYOR"} — e = ${fmt(ae, 3)} ${eOk ? "≤" : ">"} b/${divisor} = ${fmt(eLimit, 3)} m; σmax = ${fmt(sigmaMax)} ${compressionOk ? "≤" : ">"} ${fmt(m.allowableCompression)} kN/m²; kayma GK = ${fmt(shearFactor)} ${shearOk ? "≥" : "<"} ${fmt(shearTarget, 2)}`;
    const jc: JointCaseResult = { N, V, M, e, eLimit, sigmaMax, sigmaMin, eOk, compressionOk, shearFactor, shearTarget, shearOk, ok, verdict };
    result.cases[cp.id] = jc;
    if (!ok) result.allOk = false;
  }
  return result;
}
